import { Spinner } from '@/components/ui/Spinner';
import {
  buttonVariants,
  type ButtonSize,
  type ButtonVariant
} from '@/components/ui/button-variants';
import { cn } from '@/lib/cn';
import {
  cloneElement,
  isValidElement,
  type ButtonHTMLAttributes,
  type ReactElement
} from 'react';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  asChild?: boolean;
};

export function Button({
  className,
  variant = 'primary',
  size = 'md',
  fullWidth,
  loading,
  disabled,
  asChild,
  children,
  type = 'button',
  ...props
}: Props) {
  const classes = buttonVariants({ variant, size, fullWidth, className });
  const isDisabled = disabled || loading;

  if (asChild && isValidElement(children)) {
    const child = children as ReactElement<{ className?: string }>;
    return cloneElement(child, {
      className: cn(classes, child.props.className),
      ...(loading ? { 'aria-busy': true as const } : {}),
      ...(isDisabled ? { 'aria-disabled': true as const } : {})
    } as Record<string, unknown>);
  }

  return (
    <button
      type={type}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <>
          <Spinner size="sm" className="border-current/25 border-t-current" />
          <span className="opacity-90">{children}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}
